import { Link } from "@tanstack/react-router";
import { Eye, Receipt } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import type { components } from "@/api/schema";
import { EmptyState } from "@/shared/components/EmptyState";
import { Skeleton } from "@/shared/ui/skeleton";

type RedemptionRow = components["schemas"]["RedemptionRow"];

interface RedemptionTableProps {
  items: RedemptionRow[];
  isLoading: boolean;
  isError?: boolean;
}

const TH =
  "h-9 px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap select-none";
const TD = "px-4 py-3 text-sm text-[var(--text-primary)] align-middle whitespace-nowrap";

function formatDate(value?: string | null): string {
  return value ? value.slice(0, 10) : "—";
}

/** total_amount is a decimal-baht string (e.g. "440.00"); display with thousands separators. */
function formatAmount(amount: string): string {
  return Number(amount).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function RedemptionTable({ items, isLoading, isError }: RedemptionTableProps): JSX.Element {
  const { t } = useTranslation("promotion");

  if (isError || (!isLoading && items.length === 0)) {
    return (
      <div
        className="rounded-2xl border border-[var(--premium-border)] bg-white"
        data-testid="redemption-table"
      >
        <EmptyState
          icon={<Eye className="h-5 w-5" aria-hidden />}
          title={t("redemption.empty_title")}
          body={t("redemption.empty_body")}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className="space-y-2 rounded-2xl border border-[var(--premium-border)] bg-white p-4"
        data-testid="redemption-table-loading"
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-2xl border border-[var(--premium-border)] bg-white">
      <table className="w-full border-collapse" data-testid="redemption-table">
        <thead>
          <tr className="border-b border-[var(--border-subtle)]">
            <th className={TH}>{t("redemption.column.payment_date")}</th>
            <th className={TH}>{t("redemption.column.order_id")}</th>
            <th className={TH}>{t("redemption.column.status")}</th>
            <th className={`${TH} text-right`}>{t("redemption.column.total_amount")}</th>
            <th className={TH}>{t("redemption.column.seller")}</th>
            <th className={TH}>{t("redemption.column.campaign")}</th>
            <th className={TH}>{t("redemption.column.promotion")}</th>
            <th className={TH}>{t("redemption.column.rule")}</th>
            <th className={`${TH} text-right`}>{t("redemption.column.redeem_set")}</th>
            <th className={TH}>{t("redemption.column.gwp_sku")}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => (
            <tr
              key={row.usage_id}
              data-testid={`redemption-row-${row.usage_id}`}
              className="border-b border-[var(--border-subtle)] last:border-0"
            >
              <td className={`${TD} num tabular-nums text-[12.5px] text-[var(--text-secondary)]`}>
                {formatDate(row.payment_date)}
              </td>
              <td className={TD}>
                <Link
                  to="/orders/$saleOrderId"
                  params={{ saleOrderId: row.sale_order_id ?? row.order_id }}
                  data-testid={`redemption-order-${row.usage_id}`}
                  className="font-mono text-[12px] font-semibold text-[var(--brand-700)] underline-offset-2 hover:underline"
                >
                  {row.order_id}
                </Link>
              </td>
              <td className={TD}>
                <span className="inline-flex items-center gap-1 rounded-full bg-[var(--intent-success-bg)] px-2 py-0.5 text-[11px] font-bold text-[var(--intent-success-fg)]">
                  <Receipt className="h-3 w-3" aria-hidden />
                  {t("redemption.redeemed")}
                </span>
              </td>
              <td className={`${TD} num text-right font-semibold tabular-nums`}>
                {formatAmount(row.total_amount)}
              </td>
              <td className={`${TD} text-[12.5px] text-[var(--text-secondary)]`}>
                {row.seller_id ?? "—"}
              </td>
              <td className={`${TD} text-[12.5px] text-[var(--text-secondary)]`}>
                {row.campaign_name ?? "—"}
              </td>
              <td className={`${TD} text-[12px] text-[var(--text-secondary)]`}>
                {row.promotion_name ?? "—"}
              </td>
              <td className={TD}>
                <span className="rounded-md bg-[var(--surface-sunken)] px-1.5 py-0.5 text-[11px] font-bold uppercase text-[var(--text-subtle)]">
                  {t("redemption.column.rule")}
                  {row.rule_sequence ?? ""}
                </span>
              </td>
              <td className={`${TD} num text-right tabular-nums`}>{row.redeem_set}</td>
              <td className={TD}>
                <span
                  data-testid={`redemption-gwp-${row.usage_id}`}
                  className="inline-flex items-center gap-1 rounded-md bg-[var(--intent-warning-bg)] px-2 py-0.5 text-[11.5px] font-semibold text-[var(--intent-warning-fg)]"
                >
                  <span className="font-mono">{row.gwp_sku}</span>
                  <span>× {row.gwp_quantity}</span>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
